import { createSlice } from "@reduxjs/toolkit";
import localStorageAccessor from "../../../utility/local-storage-accessor";
import { sendOtp, verifyOtp, resendOtp, logoutUser, refreshAccessToken, startImpersonation, } from "../thunk/auth";
// Load impersonation state from localStorage if it exists
const loadImpersonationState = () => {
    try {
        const stored = localStorage.getItem('sitex_impersonation');
        if (stored) {
            return JSON.parse(stored);
        }
    }
    catch (e) {
        console.error('Failed to load impersonation state:', e);
    }
    return {
        isImpersonating: false,
        originalUser: null,
        originalAccessToken: null,
        originalRefreshToken: null,
        impersonatedUserId: null,
    };
};
// Save impersonation state to localStorage
const saveImpersonationState = (state) => {
    try {
        if (state.isImpersonating) {
            localStorage.setItem('sitex_impersonation', JSON.stringify(state));
        }
        else {
            localStorage.removeItem('sitex_impersonation');
        }
    }
    catch (e) {
        console.error('Failed to save impersonation state:', e);
    }
};
const initialState = {
    user: localStorageAccessor.getUser(),
    accessToken: localStorageAccessor.getAccessToken(),
    refreshToken: localStorageAccessor.getRefreshToken(),
    isAuthenticated: localStorageAccessor.isAuthenticated(),
    loading: false,
    error: null,
    errors: null,
    otpEmail: null,
    otpSent: false,
    otpLoading: false,
    resendLoading: false,
    impersonation: loadImpersonationState(),
};
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setUser: (state, action) => {
            state.user = action.payload;
            state.isAuthenticated = !!action.payload;
            // Store user data in localStorage
            if (action.payload) {
                localStorageAccessor.setUser(action.payload);
            }
            else {
                localStorageAccessor.removeUser();
            }
        },
        resetOtpState: (state) => {
            state.otpSent = false;
            state.otpLoading = false;
            state.resendLoading = false;
            state.error = null;
            state.errors = null;
        },
        clearAuthErrors: (state) => {
            state.error = null;
            state.errors = null;
        },
        stopImpersonation: (state) => {
            if (state.impersonation.isImpersonating) {
                // Restore original user
                state.user = state.impersonation.originalUser;
                state.accessToken = state.impersonation.originalAccessToken;
                state.refreshToken = state.impersonation.originalRefreshToken;
                // Update localStorage
                if (state.impersonation.originalAccessToken) {
                    localStorageAccessor.setAccessToken(state.impersonation.originalAccessToken);
                }
                if (state.impersonation.originalRefreshToken) {
                    localStorageAccessor.setRefreshToken(state.impersonation.originalRefreshToken);
                }
                if (state.impersonation.originalUser) {
                    localStorageAccessor.setUser(state.impersonation.originalUser);
                }
                // Reset impersonation state
                state.impersonation = {
                    isImpersonating: false,
                    originalUser: null,
                    originalAccessToken: null,
                    originalRefreshToken: null,
                    impersonatedUserId: null,
                };
                // Clear persisted impersonation state
                saveImpersonationState(state.impersonation);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Send OTP
            .addCase(sendOtp.pending, (state) => {
            state.otpLoading = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(sendOtp.fulfilled, (state) => {
            state.otpLoading = false;
            state.otpSent = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(sendOtp.rejected, (state, action) => {
            state.otpLoading = false;
            state.otpSent = false;
            if (typeof action.payload === "object" && action.payload !== null) {
                const errorPayload = action.payload;
                state.error = errorPayload.message || "Failed to send OTP";
                state.errors = errorPayload.errors || null;
            }
            else {
                state.error = action.payload || "Failed to send OTP";
                state.errors = null;
            }
        })
            // Verify OTP
            .addCase(verifyOtp.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(verifyOtp.fulfilled, (state, action) => {
            var _a, _b, _c;
            state.loading = false;
            state.error = null;
            state.errors = null;
            if (action.payload) {
                const userData = (_a = action.payload.data) === null || _a === void 0 ? void 0 : _a.user;
                localStorageAccessor.setApiUser(userData);
                state.user = localStorageAccessor.getUser();
                state.accessToken = (_b = action.payload.data) === null || _b === void 0 ? void 0 : _b.accessToken;
                state.refreshToken = (_c = action.payload.data) === null || _c === void 0 ? void 0 : _c.refreshToken;
                state.isAuthenticated = true;
                state.otpSent = false;
            }
        })
            .addCase(verifyOtp.rejected, (state, action) => {
            state.loading = false;
            state.isAuthenticated = false;
            if (typeof action.payload === "object" && action.payload !== null) {
                const errorPayload = action.payload;
                state.error = errorPayload.message || "OTP verification failed";
                state.errors = errorPayload.errors || null;
            }
            else {
                state.error = action.payload || "OTP verification failed";
                state.errors = null;
            }
        })
            .addCase(resendOtp.pending, (state) => {
            state.resendLoading = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(resendOtp.fulfilled, (state) => {
            state.resendLoading = false;
            state.error = null;
            state.errors = null;
        })
            .addCase(resendOtp.rejected, (state, action) => {
            state.resendLoading = false;
            if (typeof action.payload === "object" && action.payload !== null) {
                const errorPayload = action.payload;
                state.error = errorPayload.message || "Failed to resend OTP";
                state.errors = errorPayload.errors || null;
            }
            else {
                state.error = action.payload || "Failed to resend OTP";
                state.errors = null;
            }
        })
            .addCase(logoutUser.fulfilled, (state) => {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.loading = false;
            state.error = null;
            state.errors = null;
            state.otpSent = false;
            state.otpLoading = false;
            state.resendLoading = false;
            // Reset impersonation state on logout
            state.impersonation = {
                isImpersonating: false,
                originalUser: null,
                originalAccessToken: null,
                originalRefreshToken: null,
                impersonatedUserId: null,
            };
            saveImpersonationState(state.impersonation);
            // Clear user data from localStorage
            localStorageAccessor.removeUser();
        })
            // Refresh Access Token
            .addCase(refreshAccessToken.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
            var _a;
            state.loading = false;
            state.error = null;
            state.errors = null;
            if ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.data) {
                // Backend returns just the new access token as a string
                state.accessToken = action.payload.data;
                state.user = localStorageAccessor.getUser();
                state.isAuthenticated = true;
            }
        })
            .addCase(refreshAccessToken.rejected, (state, action) => {
            state.loading = false;
            state.isAuthenticated = false;
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            // Clear localStorage on refresh failure
            localStorageAccessor.removeAccessToken();
            localStorageAccessor.removeRefreshToken();
            localStorageAccessor.removeUser();
            if (typeof action.payload === "object" && action.payload !== null) {
                const errorPayload = action.payload;
                state.error = errorPayload.message || "Token refresh failed";
                state.errors = errorPayload.errors || null;
            }
            else {
                state.error = action.payload || "Token refresh failed";
                state.errors = null;
            }
        })
            // Start Impersonation
            .addCase(startImpersonation.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.errors = null;
        })
            .addCase(startImpersonation.fulfilled, (state, action) => {
            var _a;
            state.loading = false;
            state.error = null;
            state.errors = null;
            if ((_a = action.payload) === null || _a === void 0 ? void 0 : _a.data) {
                const { accessToken, user } = action.payload.data;
                // Store original user state
                state.impersonation.originalUser = state.user;
                state.impersonation.originalAccessToken = state.accessToken;
                state.impersonation.originalRefreshToken = state.refreshToken;
                state.impersonation.impersonatedUserId = (user === null || user === void 0 ? void 0 : user.id) || null;
                state.impersonation.isImpersonating = true;
                // Persist impersonation state to localStorage
                saveImpersonationState(state.impersonation);
                // Update to impersonated user
                localStorageAccessor.setApiUser(user);
                state.user = localStorageAccessor.getUser();
                state.accessToken = accessToken;
                state.isAuthenticated = true;
            }
        })
            .addCase(startImpersonation.rejected, (state, action) => {
            state.loading = false;
            if (typeof action.payload === "object" && action.payload !== null) {
                const errorPayload = action.payload;
                state.error = errorPayload.message || "Failed to start impersonation";
                state.errors = errorPayload.errors || null;
            }
            else {
                state.error = action.payload || "Failed to start impersonation";
                state.errors = null;
            }
        });
        // Stop Impersonation - handled by reducer action, not thunk
    },
});
export const { setUser, resetOtpState, clearAuthErrors, stopImpersonation } = authSlice.actions;
export default authSlice.reducer;
